export interface MapPin {
  name: string;
  lat: number;
  lng: number;
}

export const NEARBY_MAP_MAX_RADIUS_METERS = 10_000;
export const NEARBY_MAP_DEFAULT_RADIUS_METERS = 2_500;
export const NEARBY_MAP_MIN_ZOOM_IN_METERS = 120;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Haversine distance in meters — matches iOS CLLocation.distance. */
export function distanceMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): number {
  const R = 6_371_000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function isCoordinateWithinMeters(
  coordinate: { lat: number; lng: number },
  center: { lat: number; lng: number },
  maxDistance: number
): boolean {
  return distanceMeters(center, coordinate) <= maxDistance;
}

export function pinsWithinRadius(
  pins: MapPin[],
  center: { lat: number; lng: number },
  radiusMeters: number = NEARBY_MAP_MAX_RADIUS_METERS
): MapPin[] {
  return pins.filter((p) => isCoordinateWithinMeters(p, center, radiusMeters));
}

export function spanMetersForViewport(
  user: { lat: number; lng: number },
  pins: MapPin[]
): number {
  let radius = NEARBY_MAP_DEFAULT_RADIUS_METERS;
  for (const pin of pins) {
    const d = distanceMeters(user, pin);
    radius = Math.max(radius, Math.min(NEARBY_MAP_MAX_RADIUS_METERS, d * 1.35));
  }
  return Math.min(
    NEARBY_MAP_MAX_RADIUS_METERS * 2,
    Math.max(NEARBY_MAP_DEFAULT_RADIUS_METERS * 2, radius * 2)
  );
}

/** Matches iOS NearbyMapViewport.googleZoom. */
export function googleZoomForRadiusMeters(radius: number): number {
  const clamped = Math.min(
    NEARBY_MAP_MAX_RADIUS_METERS,
    Math.max(NEARBY_MAP_DEFAULT_RADIUS_METERS, radius)
  );
  if (clamped <= 1_500) return 15;
  if (clamped <= 3_000) return 14;
  if (clamped <= 6_000) return 13;
  return 12;
}

export function clampCenterDistanceMeters(distance: number): number {
  if (distance > NEARBY_MAP_MAX_RADIUS_METERS) return NEARBY_MAP_MAX_RADIUS_METERS;
  if (distance < NEARBY_MAP_MIN_ZOOM_IN_METERS) return NEARBY_MAP_MIN_ZOOM_IN_METERS;
  return distance;
}

/** Approximate Leaflet zoom for a ground span (meters) at latitude. */
export function leafletZoomForSpanMeters(lat: number, spanMeters: number, mapWidthPx = 400): number {
  const mpp = spanMeters / mapWidthPx;
  const zoom = Math.log2((40075016.686 * Math.cos(toRad(lat))) / (mpp * 256));
  return Math.max(0, Math.min(20, zoom));
}

export function leafletZoomBoundsForViewport(lat: number): { minZoom: number; maxZoom: number } {
  const minZoom = leafletZoomForSpanMeters(lat, NEARBY_MAP_MAX_RADIUS_METERS * 2);
  const maxZoom = leafletZoomForSpanMeters(lat, NEARBY_MAP_MIN_ZOOM_IN_METERS * 2);
  return { minZoom, maxZoom: Math.max(minZoom + 0.5, maxZoom) };
}
